import isEmpty from 'lodash/isEmpty';
import {
  type ITooltipParams,
  type CellClassParams,
  type HeaderClassParams,
  type HeaderValueGetterParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import {
  getAgTableHeaderName,
  getAlignStyle,
  getCustomCellStyle,
  getCustomHeaderStyle,
} from 'common/visualizations/views/agGridReact/helpers/TableColumnFormatting';
import { getFieldName } from '../helpers/getFieldName';
import { type CustomAgGridContext, type CustomColDefContext } from 'common/types/agGrid/context';
import { NULL_GROUPING } from 'common/visualizations/views/agGridReact/Constants';
import { isColDefPivoted } from './isColDefPivoted';
import { formatGroupLabelValue } from './formatGroupLabelValue';
export const valueGetterFunction = (params: ValueGetterParams) => {
  if (typeof params.data === 'undefined') {
    return;
  }
  const ctx = params.context as CustomAgGridContext;

  const { groupedColumns, showSubTotal } = ctx;
  const field = params.colDef.field ?? '';
  const isGroup = groupedColumns.includes(field);
  if (typeof params.data == 'undefined') {
    return;
  }
  const data = params.data[field];
  if (isGroup && typeof data === 'undefined') {
    return NULL_GROUPING;
  }
  // the condition below checks whether the current field
  // is part of a hierarchy row and a non-hierarchy column
  if (
    params.node?.expanded &&
    params.node?.level < groupedColumns.length &&
    !groupedColumns.includes(field) &&
    showSubTotal
  ) {
    return;
  }
  return data;
};

export const tooltipValueGetterFunction = (params: ITooltipParams) => {
  const isValueEmpty = !params.value && typeof params.value !== 'boolean';
  if (isValueEmpty) {
    return undefined;
  }
  return params.value;
};

export const headerValueGetterFunction = (params: HeaderValueGetterParams) => {
  const { columnMetadata, columnFormats } = params.context as CustomAgGridContext;

  /**
   * A pivot-key group header (e.g. a pivoted date column) carries its pivot column name in
   * `pivotHeaderColumn`. Format the raw key with that column's format so the header matches the
   * cells — e.g. a floating timestamp renders as the configured date, not `2023-01-15T00:00:00`.
   */
  const pivotHeaderColumn = (params.colDef.context as CustomColDefContext | undefined)?.pivotHeaderColumn;
  if (pivotHeaderColumn) {
    return formatGroupLabelValue({
      key: params.colDef.headerName,
      fieldName: pivotHeaderColumn,
      viewColumns: columnMetadata,
      columnFormats,
      domain: '',
      datasetUid: '',
    });
  }

  if (!isColDefPivoted(params.colDef)) {
    return params.colDef.headerName;
  }

  const fieldName = params?.colDef?.context.pivotValueColumn ?? '';
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata) {
    return params.colDef.headerName;
  }
  return getAgTableHeaderName(columnFormats[fieldName], localColumnMetadata);
};

export const cellStyleFunction = (params: CellClassParams) => {
  const ctx = params.context as CustomAgGridContext;
  const { nonStandardAggregations, columnMetadata, columnFormats, showSubTotal, currentRowStripeStyle } = ctx;
  const fieldName = getFieldName(params);
  const columnFormat = columnFormats[fieldName!];
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);

  const customCellStyle = getCustomCellStyle({
    column: localColumnMetadata!,
    nonStandardAggregations,
    columnFormat,
    params,
    showSubTotal,
    currentRowStripeStyle,
  });

  return customCellStyle;
};

export const headerClassFunction = (params: HeaderClassParams) => {
  const ctx = params.context as CustomAgGridContext;
  const { columnFormats, headerFormat } = ctx;
  const fieldName = getFieldName(params);
  const columnFormat = columnFormats[fieldName!];

  const customHeaderStyle = getCustomHeaderStyle(columnFormat);
  if (!isEmpty(customHeaderStyle)) {
    return [customHeaderStyle, 'ag-grid-header'];
  }

  // If we didn't find a style from the column, look for the global header alignment style
  const globalHeaderAlignment = headerFormat?.alignHeaders;
  if (globalHeaderAlignment && !isEmpty(globalHeaderAlignment)) {
    const alignStyle = getAlignStyle(globalHeaderAlignment);
    return [alignStyle, 'ag-grid-header'];
  }
  return ['ag-grid-header'];
};

export const pivotColumnLabelsClassFunction = (params: HeaderClassParams) => {
  const ctx = params.context as CustomAgGridContext;
  const { headerFormat } = ctx;

  const globalPivotColumnLabelsAlignment = headerFormat?.alignPivotColumnLabels;
  if (globalPivotColumnLabelsAlignment && !isEmpty(globalPivotColumnLabelsAlignment)) {
    const alignStyle = getAlignStyle(globalPivotColumnLabelsAlignment);
    return [alignStyle, 'ag-grid-header'];
  }
  return ['ag-grid-header'];
};

export const isNullGrouping = (groupedColumns: string[], value: any, field: string): boolean => {
  const isGroup = groupedColumns.includes(field);
  return isGroup && typeof value === 'undefined';
};
